'use client'

import React, { useCallback, useMemo, useState } from 'react'
import { getSession } from 'next-auth/react'
import { motion } from 'framer-motion'
import {
  Bar,
  BarChart,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { Avatar } from '@/components/ui/avatar'
import type { StreamStep } from '@/lib/api-client'
import type { ChatMessage as ChatMessageType, ToolCall } from '@/context/AIContext'

interface ChatMessageProps {
  message: ChatMessageType
  userName?: string
  userImage?: string | null
}

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''

/**
 * The success shape returned by the `generate_report` tool.
 * Mirrors contract C7 in the backend (app/services/reports/tool_payload.py).
 */
interface ReportToolResult {
  status?: string
  format?: string
  title?: string
  filename?: string
  row_count?: number
  summary?: string
  download_url?: string
  alternate_formats?: Record<string, string>
}

/** The three formats, in the order a person is most likely to want them. */
const FORMAT_LABELS: { key: string; label: string }[] = [
  { key: 'pdf', label: 'PDF' },
  { key: 'docx', label: 'Word' },
  { key: 'xlsx', label: 'Excel' },
]

function isReportDownload(tc: ToolCall): boolean {
  const result = tc.result as ReportToolResult | undefined
  return tc.name === 'generate_report' && Boolean(result?.download_url)
}

/**
 * Pull the filename the server actually chose out of Content-Disposition.
 *
 * The backend sanitises and dates the name, so honouring it means the file in the
 * Downloads folder matches the name shown next to the button. Falls back to the
 * name the tool advertised, and then to a generic one, because a download that
 * lands as "download" is still better than one that does not land.
 */
function filenameFromResponse(response: Response, fallback: string): string {
  const header = response.headers.get('content-disposition') || ''
  const match = header.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)
  if (!match) return fallback
  try {
    return decodeURIComponent(match[1])
  } catch {
    // A stray percent sign makes decodeURIComponent throw. That must not cost the
    // user the file: the bytes are already here and the token is already spent.
    return match[1]
  }
}

/**
 * Download buttons for a generated report.
 *
 * WHY A BLOB AND NOT A LINK
 * -------------------------
 * This used to render the string "Used 1 tool" while the model wrote a literal
 * markdown link to the text "download_url", so the one thing the user wanted (the
 * file) was the one thing they could not get. A plain anchor cannot work either:
 * a navigation carries no Authorization header. So the click fetches the bytes
 * with the session token, wraps them in an object URL and clicks a synthetic
 * anchor carrying `download`. That is what puts the file in the Downloads folder.
 *
 * Each format URL carries its own single-use token, which is why a spent link
 * reports "expired or already used" rather than silently doing nothing.
 */
function ReportDownloads({ toolCall }: { toolCall: ToolCall }) {
  const result = toolCall.result as ReportToolResult
  const [busyFormat, setBusyFormat] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Prefer the per-format map; a result that only carries a single download_url
  // still renders one working button. Memoised so the download callback below is
  // not rebuilt on every keystroke elsewhere in the chat.
  const urls: Record<string, string> = useMemo(
    () =>
      result.alternate_formats && Object.keys(result.alternate_formats).length > 0
        ? result.alternate_formats
        : { [result.format || 'pdf']: result.download_url as string },
    [result.alternate_formats, result.format, result.download_url]
  )

  const available = FORMAT_LABELS.filter((f) => Boolean(urls[f.key]))

  const download = useCallback(
    async (formatKey: string) => {
      const relativeUrl = urls[formatKey]
      if (!relativeUrl) return

      const fullUrl = `${API_URL}${BASE_PATH}${relativeUrl}`
      setBusyFormat(formatKey)
      setError(null)

      try {
        const session = await getSession()
        // The download route authorises on the signed token, not the header, but
        // sending the bearer anyway keeps the request identical to every other
        // call this app makes and keeps the audit log attributed to a person.
        let response = await fetch(fullUrl, {
          headers: session?.accessToken
            ? { Authorization: `Bearer ${session.accessToken}` }
            : {},
        })

        // A stale access token is rejected by the global auth dependency before the
        // route body ever runs, so the download token is still unspent. The route is
        // public precisely so a header is not needed: retry once without one rather
        // than telling the user their report failed because their session aged out.
        if (response.status === 401) {
          response = await fetch(fullUrl)
        }

        if (!response.ok) {
          if (response.status === 410) {
            setError('That download link has expired or was already used. Ask for the report again.')
          } else if (response.status === 403) {
            setError('That download link is not valid. Ask for the report again.')
          } else {
            setError('The download failed. Please try again.')
          }
          return
        }

        const blob = await response.blob()
        const fallbackName = result.filename || `supervity-report.${formatKey}`
        const objectUrl = URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = objectUrl
        anchor.download = filenameFromResponse(response, fallbackName)
        document.body.appendChild(anchor)
        anchor.click()
        document.body.removeChild(anchor)
        URL.revokeObjectURL(objectUrl)
      } catch {
        // A network or CORS failure, not a rejected token. Opening the URL lets the
        // browser do the download itself, which works because the route is public.
        window.open(fullUrl, '_blank')
      } finally {
        setBusyFormat(null)
      }
    },
    [urls, result.filename]
  )

  const rowCount = typeof result.row_count === 'number' ? result.row_count : null

  return (
    <div className='mt-1 flex w-full flex-col gap-1.5 rounded-xl border border-border/50 bg-white/70 px-3 py-2.5'>
      <div className='flex items-start gap-2'>
        <Icons.fileText className='mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-brand-purple' strokeWidth={1.5} />
        <div className='min-w-0'>
          <p className='truncate text-xs font-medium text-foreground'>
            {result.title || 'Report'}
          </p>
          <p className='truncate text-[12px] text-muted-foreground'>
            {result.filename || 'Ready to download'}
            {rowCount !== null && ` · ${rowCount} row${rowCount === 1 ? '' : 's'}`}
          </p>
        </div>
      </div>

      <div className='flex flex-wrap gap-1.5'>
        {available.map((f) => (
          <button
            key={f.key}
            type='button'
            onClick={() => download(f.key)}
            disabled={busyFormat !== null}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[13px] font-medium',
              'border border-brand-cornflower/25 bg-brand-cornflower/10 text-brand-navy',
              'transition-colors hover:bg-brand-cornflower/20',
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
          >
            {busyFormat === f.key ? (
              <Icons.loader className='h-3 w-3 animate-spin' strokeWidth={1.5} />
            ) : (
              <Icons.download className='h-3 w-3' strokeWidth={1.5} />
            )}
            {f.label}
          </button>
        ))}
      </div>

      {error && (
        <div className='flex items-start gap-1.5 text-[12px] text-destructive'>
          <Icons.alertCircle className='mt-0.5 h-3 w-3 flex-shrink-0' strokeWidth={1.5} />
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/**
 * Render inline markdown (bold, code, italic) in text
 */
function renderInlineMarkdown(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = []
  // Match **bold**, `code`, and *italic*
  const regex = /(\*\*[^*]+\*\*)|(`[^`]+`)|(\*[^*]+\*)/g
  let lastIndex = 0
  let match
  let keyIndex = 0

  while ((match = regex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    const segment = match[0]
    if (segment.startsWith('**') && segment.endsWith('**')) {
      // Bold
      parts.push(
        <strong key={keyIndex++} className="font-semibold text-foreground">
          {segment.slice(2, -2)}
        </strong>
      )
    } else if (segment.startsWith('`') && segment.endsWith('`')) {
      // Code
      parts.push(
        <code
          key={keyIndex++}
          className="px-1.5 py-0.5 bg-muted/50 rounded text-xs font-mono text-brand-navy"
        >
          {segment.slice(1, -1)}
        </code>
      )
    } else if (segment.startsWith('*') && segment.endsWith('*')) {
      // Italic
      parts.push(
        <em key={keyIndex++} className="italic">
          {segment.slice(1, -1)}
        </em>
      )
    }
    lastIndex = regex.lastIndex
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : [text]
}

/**
 * CHARTS AND TABLES INSIDE AN ANSWER
 * ----------------------------------
 * Added 2026-09-22, for the deterministic finance and procurement answers described in
 * docs/fixes/03-ai-manager-deterministic-answers.md. That plan recorded the gap plainly:
 * `MarkdownContent` "has no table support and no chart support. A markdown table pasted
 * into an answer today renders as rows of pipe characters."
 *
 * The same plan settled the form: "Horizontal bars with printed values are the right
 * default here. They read from the back of a room, they survive a projector, and they
 * need no axis to be understood."
 *
 * So the chart drawn below has no value axis and no gridlines at all. Every bar carries
 * its own number at the tip. That is also the relief the dataviz guidance requires for a
 * light fill sitting under 3:1 contrast against a white chat bubble: the value is visible
 * as text, so nothing is gated behind telling two fills apart.
 *
 * Nothing here invents a number. The renderer only formats what the answer sent it, and a
 * value it cannot read is dropped rather than guessed.
 */

/**
 * Series colours, in fixed slot order, never cycled.
 *
 * Slots 1 and 2 are the brand navy and cornflower from tailwind.config.js (#141A42 and
 * #8AA2DF), stepped until they clear the dataviz checks against a white bubble: the raw
 * navy sits below the lightness band and the raw cornflower below the chroma floor, so
 * the chart steps are #3B4795 and #7E9AE8. Slots 3 and 4 are taken from the dataviz
 * reference palette, because the brand has no third and fourth hue to step.
 *
 * Validated 2026-09-22 with the dataviz validator on surface #FFFFFF with --pairs all:
 * every check passes, worst pair CVD dE 9.1, worst pair normal vision dE 21.0, with the
 * contrast warning relieved by the printed value on each bar.
 *
 * A fifth series is not given an invented hue and is not painted the same colour as an
 * earlier one. It is named under the chart instead.
 */
const CHART_SERIES_COLORS = ['#3B4795', '#7E9AE8', '#1BAF7A', '#EDA100']

/**
 * Chart ink, from the dataviz chrome table. Text never wears a series colour; the swatch
 * next to the text is what carries identity.
 */
const CHART_INK = '#0B0B0B'
const CHART_INK_SOFT = '#52514E'
const CHART_BASELINE = '#C3C2B7'

/** The JSON an answer is allowed to put inside a ```chart fence, before validation. */
interface ChartSpecInput {
  kind?: unknown
  title?: unknown
  unit?: unknown
  series?: unknown
  rows?: unknown
  reference?: unknown
}

/** The same chart once every field has been checked, which is the only shape drawn. */
interface ParsedChart {
  title: string | null
  unit: string
  series: string[]
  rows: { label: string; values: number[] }[]
  reference: { label: string; value: number } | null
}

/**
 * Read one ```chart body.
 *
 * Returns null for anything that is not exactly the agreed shape, and the caller then
 * renders the block as ordinary text. This runs live on stage, so a mistyped brace must
 * cost a chart and never the whole answer. Hence the try/catch and the field-by-field
 * checks rather than a cast.
 */
function parseChartSpec(body: string): ParsedChart | null {
  let raw: unknown
  try {
    raw = JSON.parse(body)
  } catch {
    return null
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null

  const spec = raw as ChartSpecInput
  // "kind" may be omitted; grouped horizontal bars are the only drawing this knows.
  if (spec.kind !== undefined && spec.kind !== 'bar') return null
  if (!Array.isArray(spec.series) || spec.series.length === 0) return null
  if (!Array.isArray(spec.rows) || spec.rows.length === 0) return null

  const series: string[] = []
  for (const name of spec.series) {
    if (typeof name !== 'string') return null
    series.push(name)
  }

  const rows: { label: string; values: number[] }[] = []
  for (const entry of spec.rows) {
    if (!entry || typeof entry !== 'object') return null
    const row = entry as { label?: unknown; values?: unknown }
    if (typeof row.label !== 'string' || !Array.isArray(row.values)) return null
    const values: number[] = []
    for (const value of row.values) {
      if (typeof value !== 'number' || !Number.isFinite(value)) return null
      values.push(value)
    }
    rows.push({ label: row.label, values })
  }

  let reference: ParsedChart['reference'] = null
  if (spec.reference && typeof spec.reference === 'object') {
    const ref = spec.reference as { label?: unknown; value?: unknown }
    if (typeof ref.value === 'number' && Number.isFinite(ref.value)) {
      reference = { label: typeof ref.label === 'string' ? ref.label : '', value: ref.value }
    }
  }

  return {
    title: typeof spec.title === 'string' ? spec.title : null,
    unit: typeof spec.unit === 'string' ? spec.unit : '',
    series,
    rows,
    reference,
  }
}

/** Drop a trailing ".0" so £6.0m reads £6m and 22.0% reads 22%. */
function trimTrailingZero(text: string): string {
  return text.endsWith('.0') ? text.slice(0, -2) : text
}

/**
 * Format one value for the tip of its bar.
 *
 * GBP arrives as plain integers in pounds, so the rounding happens here and only here:
 * millions to one decimal (6,560,000 reads £6.6m), thousands to a whole number above
 * £10k and to one decimal below it, so £1,500 does not read as £2k. This is presentation
 * rounding of a figure the answer supplied; the answer text carries the exact number
 * wherever the exact number matters.
 */
function formatChartValue(value: number, unit: string): string {
  if (unit === 'GBP') {
    const sign = value < 0 ? '-' : ''
    const abs = Math.abs(value)
    if (abs >= 1000000) return sign + '£' + trimTrailingZero((abs / 1000000).toFixed(1)) + 'm'
    if (abs >= 10000) return sign + '£' + Math.round(abs / 1000).toLocaleString('en-GB') + 'k'
    if (abs >= 1000) return sign + '£' + trimTrailingZero((abs / 1000).toFixed(1)) + 'k'
    return sign + '£' + Math.round(abs).toLocaleString('en-GB')
  }
  if (unit === '%') return trimTrailingZero(value.toFixed(1)) + '%'
  if (unit === 'people') return Math.round(value).toLocaleString('en-GB')
  return value.toLocaleString('en-GB')
}

/**
 * Grouped horizontal bars, drawn for a projector.
 *
 * Deliberate choices, all from the dataviz guidance: bars at most 24px thick with a 2px
 * surface gap between them, a 4px rounded data end squared off at the baseline, a zero
 * baseline on every chart whatever the values are, no value axis and no gridlines, row
 * labels on the left in soft ink, and the value printed outside the tip of each bar so a
 * label can never be clipped by its own bar. Animation is off: it buys nothing on a
 * projector and it makes a screenshot in testing non-deterministic.
 */
function AnswerChart({ chart }: { chart: ParsedChart }) {
  const { rows, unit, reference } = chart

  // At most four series are drawn, because there are four validated slots. A fifth would
  // either repeat a colour or invent one, and both make the chart lie about identity.
  const drawn = chart.series.slice(0, CHART_SERIES_COLORS.length)
  const undrawn = chart.series.slice(CHART_SERIES_COLORS.length)

  const data = rows.map((row) => {
    const point: Record<string, string | number> = { label: row.label }
    drawn.forEach((_name, index) => {
      const value = row.values[index]
      if (typeof value === 'number') point['v' + index] = value
    })
    return point
  })

  const values = rows.flatMap((row) => row.values.slice(0, drawn.length))
  // Zero is always in the domain, and the top gets 4% of headroom so the longest bar
  // still leaves room for its own printed value.
  const maxValue = Math.max(0, ...values, reference ? reference.value : 0)
  const minValue = Math.min(0, ...values)
  const domain: [number, number] = [minValue, maxValue === 0 ? 1 : maxValue * 1.04]

  const barSize = drawn.length > 2 ? 18 : 22
  const rowHeight = drawn.length * (barSize + 2) + 26
  const height = rows.length * rowHeight + (reference ? 44 : 20)
  const longestLabel = Math.max(...rows.map((row) => row.label.length))
  const labelWidth = Math.min(200, Math.max(84, Math.round(longestLabel * 8.5)))

  return (
    <figure className='my-3 w-full'>
      {chart.title && (
        <figcaption className='mb-1 font-semibold text-foreground'>{chart.title}</figcaption>
      )}

      {/* A legend is present whenever there are two or more series, so identity never
          rests on colour matching alone. One series needs none: the title names it. */}
      {drawn.length > 1 && (
        <div className='mb-2 flex flex-wrap items-center gap-x-4 gap-y-1'>
          {drawn.map((name, index) => (
            <span
              key={name}
              className='flex items-center gap-1.5 text-[0.8em] text-muted-foreground'
            >
              <span
                className='h-2.5 w-2.5 flex-shrink-0 rounded-sm'
                style={{ backgroundColor: CHART_SERIES_COLORS[index] }}
              />
              {name}
            </span>
          ))}
        </div>
      )}

      <div style={{ width: '100%', height }}>
        <ResponsiveContainer width='100%' height='100%'>
          <BarChart
            data={data}
            layout='vertical'
            margin={{ top: reference ? 22 : 4, right: 76, bottom: 4, left: 0 }}
            barGap={2}
            barCategoryGap={18}
          >
            {/* The value axis exists so the bars have a scale; it is never drawn. */}
            <XAxis type='number' domain={domain} hide />
            <YAxis
              type='category'
              dataKey='label'
              width={labelWidth}
              tickLine={false}
              axisLine={{ stroke: CHART_BASELINE }}
              tick={{ fill: CHART_INK_SOFT, fontSize: 15 }}
            />
            {drawn.map((name, index) => (
              <Bar
                key={name}
                dataKey={'v' + index}
                name={name}
                fill={CHART_SERIES_COLORS[index]}
                barSize={barSize}
                radius={[0, 4, 4, 0]}
                isAnimationActive={false}
              >
                <LabelList
                  dataKey={'v' + index}
                  position='right'
                  offset={8}
                  fill={CHART_INK}
                  fontSize={15}
                  fontWeight={600}
                  formatter={(label) => formatChartValue(Number(label), unit)}
                />
              </Bar>
            ))}
            {reference && (
              <ReferenceLine
                x={reference.value}
                stroke={CHART_INK_SOFT}
                strokeWidth={2}
                label={{
                  value: reference.label || formatChartValue(reference.value, unit),
                  position: 'top',
                  fill: CHART_INK_SOFT,
                  fontSize: 14,
                }}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {undrawn.length > 0 && (
        <p className='mt-1 text-[0.8em] text-muted-foreground'>
          Not drawn: {undrawn.join(', ')}
        </p>
      )}
    </figure>
  )
}

/** One markdown table, already split into cells. */
interface ParsedTable {
  header: string[]
  aligns: ('left' | 'center' | 'right')[]
  rows: string[][]
}

/** Split "| a | b |" into its cells, tolerating a missing leading or trailing pipe. */
function splitPipeRow(line: string): string[] {
  let text = line.trim()
  if (text.startsWith('|')) text = text.slice(1)
  if (text.endsWith('|')) text = text.slice(0, -1)
  return text.split('|').map((cell) => cell.trim())
}

/** A line that could be a table row: a leading pipe, or at least two of them. */
function isPipeRow(line: string): boolean {
  const text = line.trim()
  if (!text.includes('|')) return false
  return text.startsWith('|') || text.split('|').length >= 3
}

/** The "| --- | ---: |" line directly under a header, which is what makes it a table. */
function isTableSeparator(line: string): boolean {
  const text = line.trim()
  if (!text.includes('-')) return false
  return /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?$/.test(text)
}

/** Read the alignment markers out of the separator row. */
function readAlignments(line: string): ('left' | 'center' | 'right')[] {
  return splitPipeRow(line).map((cell) => {
    const left = cell.startsWith(':')
    const right = cell.endsWith(':')
    if (left && right) return 'center'
    if (right) return 'right'
    return 'left'
  })
}

/**
 * A markdown table.
 *
 * Figures are tabular so a column of numbers lines up vertically, which is the one place
 * the dataviz guidance asks for tabular figures.
 */
function AnswerTable({ table }: { table: ParsedTable }) {
  const align = (index: number) => table.aligns[index] || 'left'

  return (
    <div className='my-2 w-full overflow-x-auto'>
      <table className='w-full border-collapse text-[0.9em]'>
        <thead>
          <tr>
            {table.header.map((cell, index) => (
              <th
                key={index}
                className='border-b border-border/60 px-2 py-1.5 font-semibold text-foreground'
                style={{ textAlign: align(index) }}
              >
                {renderInlineMarkdown(cell)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, rowIndex) => (
            <tr key={rowIndex} className='border-b border-border/30 last:border-0'>
              {row.map((cell, index) => (
                <td
                  key={index}
                  className='px-2 py-1.5 align-top tabular-nums'
                  style={{ textAlign: align(index) }}
                >
                  {renderInlineMarkdown(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** What one pass over an answer produces: charts, tables, and everything else per line. */
type AnswerBlock =
  | { type: 'chart'; chart: ParsedChart }
  | { type: 'table'; table: ParsedTable }
  | { type: 'line'; text: string }

/**
 * Walk an answer once and pull out the two things that span more than one line.
 *
 * Everything this does not recognise comes back as the individual lines the renderer has
 * always handled, so an answer with no chart and no table renders exactly as before.
 *
 * Three ways this degrades rather than fails, all of them deliberate: a ```chart fence
 * whose JSON does not parse or does not match the contract comes back as its own text
 * lines; an unterminated fence comes back as text; and a pipe row with no separator under
 * it is left alone, because it is prose, not a table.
 */
function splitAnswerBlocks(content: string): AnswerBlock[] {
  const lines = content.split('\n')
  const blocks: AnswerBlock[] = []
  let i = 0

  while (i < lines.length) {
    const trimmed = lines[i].trim()

    if (/^```chart\s*$/.test(trimmed)) {
      let end = i + 1
      while (end < lines.length && lines[end].trim() !== '```') end++
      if (end < lines.length) {
        const chart = parseChartSpec(lines.slice(i + 1, end).join('\n'))
        if (chart) {
          blocks.push({ type: 'chart', chart })
        } else {
          for (let k = i; k <= end; k++) blocks.push({ type: 'line', text: lines[k] })
        }
        i = end + 1
        continue
      }
      // No closing fence. Fall through and treat the rest as ordinary text.
    }

    if (isPipeRow(lines[i]) && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const header = splitPipeRow(lines[i])
      const aligns = readAlignments(lines[i + 1])
      const rows: string[][] = []
      let cursor = i + 2
      while (cursor < lines.length && isPipeRow(lines[cursor])) {
        rows.push(splitPipeRow(lines[cursor]))
        cursor++
      }
      blocks.push({ type: 'table', table: { header, aligns, rows } })
      i = cursor
      continue
    }

    blocks.push({ type: 'line', text: lines[i] })
    i++
  }

  return blocks
}

/**
 * Render markdown content with support for headers, lists, blockquotes.
 *
 * Exported because the Command Briefing's ask box answers in place now, and an
 * answer rendered by a second, slightly different parser is an answer that
 * formats differently depending on which box you typed into. `className`
 * carries the size, which is the only thing the two callers disagree about.
 */
export function MarkdownContent({
  content,
  className = 'text-[17px]',
}: {
  content: string
  className?: string
}) {
  // Charts and tables span several lines, so the answer is walked once for those first.
  // Everything else comes back as the plain lines this renderer always worked on.
  const blocks = useMemo(() => splitAnswerBlocks(content), [content])

  return (
    <div className={cn('space-y-2 leading-relaxed', className)}>
      {blocks.map((block, i) => {
        if (block.type === 'chart') {
          return <AnswerChart key={i} chart={block.chart} />
        }
        if (block.type === 'table') {
          return <AnswerTable key={i} table={block.table} />
        }

        const line = block.text
        const trimmedLine = line.trim()
        
        // Empty line - add spacing
        if (!trimmedLine) {
          return <div key={i} className="h-1" />
        }
        
        // Headers (##, ###)
        if (trimmedLine.startsWith('### ')) {
          return (
            <h4 key={i} className="font-semibold text-foreground mt-3 first:mt-0">
              {renderInlineMarkdown(trimmedLine.slice(4))}
            </h4>
          )
        }
        if (trimmedLine.startsWith('## ')) {
          return (
            <h3 key={i} className="font-bold text-foreground mt-3 first:mt-0">
              {renderInlineMarkdown(trimmedLine.slice(3))}
            </h3>
          )
        }
        
        // Blockquote
        if (trimmedLine.startsWith('> ')) {
          return (
            <blockquote
              key={i}
              className="pl-3 border-l-2 border-brand-cornflower/50 text-muted-foreground italic"
            >
              {renderInlineMarkdown(trimmedLine.slice(2))}
            </blockquote>
          )
        }
        
        // Bullet points (* or -)
        if (/^[*\-]\s/.test(trimmedLine)) {
          return (
            <div key={i} className="flex gap-2 pl-1">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-cornflower/60 mt-2 flex-shrink-0" />
              <span>{renderInlineMarkdown(trimmedLine.replace(/^[*\-]\s/, ''))}</span>
            </div>
          )
        }
        
        // Numbered lists
        if (/^\d+\.\s/.test(trimmedLine)) {
          const match = trimmedLine.match(/^(\d+)\.\s(.*)/)
          if (match) {
            return (
              <div key={i} className="flex gap-2 pl-1">
                <span className="text-brand-cornflower font-medium min-w-[1.25rem]">
                  {match[1]}.
                </span>
                <span>{renderInlineMarkdown(match[2])}</span>
              </div>
            )
          }
        }
        
        // Regular paragraph
        return <p key={i}>{renderInlineMarkdown(trimmedLine)}</p>
      })}
    </div>
  )
}

/**
 * THE THINKING PANEL
 * ------------------
 * Added 2026-09-22. The brief was plain: the answer should "show it is thinking, looking
 * at this information, looking at that information, searching across systems", so what
 * replaces the three bouncing dots is a list of the work actually done, not a prettier
 * spinner.
 *
 * Every step stays on screen once it has appeared, so by the time the answer starts the
 * room can read the whole route the system took. The step being worked on wears the brand
 * cornflower and a soft dot; a finished one drops back to muted ink and a tick. Sizes are
 * set for a projector at the back of a room, which is why the label is 17px and the rows
 * are spaced at 12px rather than the usual list spacing.
 *
 * Motion is one fade and a four pixel rise as a step appears, and nothing else. Framer
 * Motion only animates on mount, and the key is the label, so a step being ticked off does
 * not replay its entrance.
 */
function ThinkingSteps({ steps }: { steps: StreamStep[] }) {
  return (
    <ol className='flex flex-col gap-3 py-1' aria-live='polite'>
      {steps.map((step) => {
        const done = step.state === 'done'
        return (
          <motion.li
            key={step.label}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className='flex items-start gap-3'
          >
            <span
              className={cn(
                'mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border',
                done
                  ? 'border-border/60 bg-muted/40'
                  : 'border-brand-cornflower bg-brand-cornflower/10'
              )}
            >
              {done ? (
                <Icons.check className='h-3.5 w-3.5 text-muted-foreground' strokeWidth={2.5} />
              ) : (
                <span className='h-2 w-2 rounded-full bg-brand-purple motion-safe:animate-pulse' />
              )}
            </span>

            <span className='flex flex-col gap-0.5'>
              {/* The accent on the running step is the brand cornflower, but it is worn
                  by the dot and the ring rather than by the label. #8AA2DF as text sits
                  at 2.5:1 on a white bubble, which is a wash on a projector; the brand
                  purple #535EA4 is the same family at 5.9:1, so the step is still read
                  as the cornflower accent and is still readable from the back. */}
              <span
                className={cn(
                  'text-[17px] leading-snug',
                  done ? 'text-muted-foreground' : 'font-semibold text-brand-purple'
                )}
              >
                {step.label}
              </span>
              {step.detail && (
                <span className='text-[14px] leading-snug text-muted-foreground/80'>
                  {step.detail}
                </span>
              )}
            </span>
          </motion.li>
        )
      })}
    </ol>
  )
}

/**
 * Hide the one block at the end of a streamed answer that has not finished arriving.
 *
 * Added 2026-09-22 with the streamed AI Manager answers. `splitAnswerBlocks` degrades an
 * unterminated ```chart fence to its own text lines, which is right for a broken answer
 * and wrong for one that is merely half here: every fragment of the chart JSON would flash
 * on screen as a line of raw braces before the closing fence arrives and the chart draws.
 * The same applies to a table, whose header row renders as a paragraph of pipe characters
 * until the "| --- |" line lands under it.
 *
 * So a trailing open chart fence and a trailing pipe row are held back until they close.
 * Nothing is lost: the caller clears `isStreaming` when the stream ends and the full
 * content renders on that pass.
 */
function trimUnfinishedBlocks(content: string): string {
  const lines = content.split('\n')

  // Walk back to whichever comes first: an opening ```chart with nothing closing it, or a
  // closing ``` which proves the last fence is already complete.
  for (let i = lines.length - 1; i >= 0; i--) {
    const trimmed = lines[i].trim()
    if (/^```chart\s*$/.test(trimmed)) return lines.slice(0, i).join('\n')
    if (trimmed === '```') break
  }

  const last = lines.length > 0 ? lines[lines.length - 1].trim() : ''

  // A fragment boundary can land inside the fence marker itself, leaving "```cha" as the
  // final line for one frame. A bare "```" is left alone, because that one is the closing
  // fence of a chart that has just completed and dropping it would unterminate it.
  if (last.startsWith('```') && last !== '```') {
    return lines.slice(0, -1).join('\n')
  }

  if (lines.length > 0 && isPipeRow(lines[lines.length - 1])) {
    return lines.slice(0, -1).join('\n')
  }

  return content
}

export function ChatMessage({ message, userName, userImage }: ChatMessageProps) {
  const isUser = message.role === 'user'
  const isLoading = message.isLoading

  // Steps only ever arrive on the deterministic path. With none, this message renders
  // exactly as it did before: the three bouncing dots while it waits, then the answer.
  const steps = message.steps
  const hasSteps = Boolean(steps && steps.length > 0)
  const body = message.isStreaming ? trimUnfinishedBlocks(message.content) : message.content

  return (
    <div
      className={cn(
        'flex gap-3',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar */}
      <div className="flex-shrink-0">
        {isUser ? (
          <Avatar
            src={userImage}
            fallback={userName || 'U'}
            size="sm"
          />
        ) : (
          <div className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full',
            'bg-gradient-to-br from-brand-navy to-brand-purple',
            'shadow-soft'
          )}>
            <Icons.sparkles className="h-4 w-4 text-white" strokeWidth={1.5} />
          </div>
        )}
      </div>

      {/* Message Content */}
      <div className={cn(
        'flex max-w-[85%] flex-col gap-1',
        isUser ? 'items-end' : 'items-start'
      )}>
        <div
          className={cn(
            'rounded-2xl px-4 py-3',
            'transition-all duration-200',
            isUser
              ? 'bg-brand-navy text-white rounded-br-md'
              : 'bg-white/90 text-foreground border border-border/50 rounded-bl-md shadow-soft',
            // The pulse is the waiting state. Once named steps are on screen there is
            // something real to read, and a pulsing bubble would only make it harder.
            isLoading && !hasSteps && 'animate-pulse'
          )}
        >
          {isLoading && !hasSteps ? (
            <div className="flex items-center gap-2 py-1">
              <div className="flex gap-1">
                <span className="h-2 w-2 animate-bounce rounded-full bg-brand-muted/60" style={{ animationDelay: '0ms' }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-brand-muted/60" style={{ animationDelay: '150ms' }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-brand-muted/60" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-[15px] text-brand-muted">Thinking...</span>
            </div>
          ) : isUser ? (
            <p className="text-[15px] whitespace-pre-wrap leading-relaxed">
              {message.content}
            </p>
          ) : (
            <>
              {/* The steps sit above the answer and stay there once it has landed: they
                  are where the answer came from, which is worth more on stage than the
                  couple of lines of height they cost. */}
              {steps && hasSteps && <ThinkingSteps steps={steps} />}

              {body.trim() && (
                <MarkdownContent
                  content={body}
                  className={cn('text-[17px]', hasSteps && 'mt-3 border-t border-border/40 pt-3')}
                />
              )}
            </>
          )}
        </div>

        {/* Timestamp */}
        <span className={cn(
          'text-[12px] text-muted-foreground/60',
          isUser ? 'pr-1' : 'pl-1'
        )}>
          {formatTime(message.timestamp)}
        </span>

        {/* Tool results. A generated report becomes a real download button group;
            anything else stays the quiet "used N tools" note it always was. The
            note is suppressed once a report is on screen, because a download panel
            already tells the user the AI did something. */}
        {message.toolCalls && message.toolCalls.length > 0 && (
          <div className="flex w-full flex-col gap-1.5 pl-1">
            {message.toolCalls.filter(isReportDownload).map((tc) => (
              <ReportDownloads key={tc.id} toolCall={tc} />
            ))}

            {!message.toolCalls.some(isReportDownload) && (
              <div className="flex items-center gap-1.5 text-[12px] text-brand-cornflower">
                <Icons.zap className="h-3 w-3" strokeWidth={1.5} />
                <span>Used {message.toolCalls.length} tool{message.toolCalls.length > 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
